"use client";

import clsx from "clsx";
import { useMemo } from "react";
import { SingleProductData } from "@repo/apis/single-product";
import * as Container from "@repo/ui/container";
import { ErrorBoundary } from "@repo/ui/error-boundary";
import * as List from "@repo/ui/list";
import { useRecommendations } from "@repo/ui/useRecommendations";
import { ProductCarousel } from "@/components/product-carousel";
import { useSmallViewport } from "@/components/viewport";
import { CAROUSEL, BRAND_FIT_INDICATORS } from "@/constants";
import {
  TYPOGRAPHY_HEADING1,
  TYPOGRAPHY_HEADING4,
  TYPOGRAPHY_BODY3,
} from "@/theme/topshop/typography/typography-constants";
import { clientRequest } from "@/utils/request-adapters/client-request";
import {
  ViewItemListProductAnalytics,
  ViewItemListProductAnalyticsProps,
} from "../analytics/view-item-list-product-analytics";
import classes from "./recommendations.module.css";

type RecommendationsProps = {
  productId: SingleProductData["id"];
  store: string;
  country: string;
  lang: string;
  currency: string;
  type: "ymal" | "pab";
  title: string;
  page: "pdp" | "plp";
};

export function isValidRecommendation(product: unknown): boolean {
  return (
    typeof product === "object" &&
    product !== null &&
    "id" in product &&
    "name" in product &&
    "price" in product &&
    "imageUrl" in product &&
    "brandName" in product
  );
}

export const Recommendations = ({
  productId,
  store,
  country,
  lang,
  currency,
  type,
  title,
  page,
}: RecommendationsProps) => {
  const joinedProductIds = useMemo(() => [productId.toString()], [productId]);
  const isMobile = useSmallViewport();

  const { recommendations, recommendedState } = useRecommendations({
    store,
    country,
    lang,
    currency,
    productids: joinedProductIds,
    chameleonRequestInstance: clientRequest,
    type,
    withBrandName: false,
    limit: 16,
    shop: "topshop",
    fitIndicators: BRAND_FIT_INDICATORS,
  });

  if (recommendedState !== "success") {
    return null;
  }

  const validRecommendations = recommendations?.filter(isValidRecommendation);
  const showRecommendations =
    validRecommendations && validRecommendations.length >= 4;

  if (!showRecommendations) return null;

  return (
    <ErrorBoundary>
      <section>
        <h2 className={clsx(TYPOGRAPHY_HEADING1, classes.title)}>{title}</h2>
        <Container.Break asChild>
          <List.Root snap snapOffset="var(--container-gutter)">
            <ProductCarousel
              products={recommendations}
              store={store}
              country={country}
              currency={currency}
              sourceSetProfile={CAROUSEL}
              type={type}
              listItemStyle={{
                width: isMobile ? 180 : 300,
              }}
              productTitleClassName={TYPOGRAPHY_HEADING4}
              productPriceClassName={TYPOGRAPHY_BODY3}
              includeBrandInUrl={true}
            />
          </List.Root>
        </Container.Break>
        <ViewItemListProductAnalytics
          recommendations={recommendations}
          item_list_name={
            `${page}_${type}` as ViewItemListProductAnalyticsProps["item_list_name"]
          }
        />
      </section>
    </ErrorBoundary>
  );
};
